import { type RefObject, useEffect, useRef } from 'react';

import type { Point2, ReadableStore } from '@frontend/viewer-core';

import { MEASURE_COLOR, SNAP_RING_RADIUS_PX } from '../constants';
import type { MeasureState, PointerState, ToolId } from '../session/types';
import {
  type MeasurementFormat,
  formatLength,
} from '../utils/formatMeasurement';
import {
  type ToolViewport,
  bindOverlayFrame,
  create,
  createLabelledLine,
  placeLabelledLine,
  setAttributes,
  show,
} from '../utils/toolUtils';

export type MeasureOverlayDeps = {
  measure: ReadableStore<MeasureState>;
  tool: ReadableStore<ToolId>;
  pointer: ReadableStore<PointerState>;
  viewport: ToolViewport;
  format: MeasurementFormat;
};

/**
 * Measure tool: a labelled distance and the snap ring under the cursor.
 *
 * Committed ends live on the reactive tier; the rubber-band end and snap follow
 * the pointer on the live tier. Drawn in SVG so the length is real text, and
 * rewritten per frame so a move never commits React.
 */
export const useDxfMeasureOverlay = ({
  measure,
  tool,
  pointer,
  viewport,
  format,
}: MeasureOverlayDeps): RefObject<SVGSVGElement> => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const measureLine = createLabelledLine(MEASURE_COLOR, true);
    svg.appendChild(measureLine.group);

    const snapRing = create('circle');
    setAttributes(snapRing, {
      r: SNAP_RING_RADIUS_PX,
      fill: 'none',
      stroke: MEASURE_COLOR,
      'stroke-width': 1.5,
    });
    svg.appendChild(snapRing);

    const draw = () => {
      const { from, to } = measure.getState();
      const pointerState = pointer.getState();
      const isMeasuring = tool.getState() === 'measure';
      const measureEnd: Point2 | null =
        to ?? (isMeasuring ? pointerState.world : null);

      const hasMeasureLine = Boolean(from && measureEnd);
      show(measureLine.group, hasMeasureLine);
      if (from && measureEnd) {
        const distance = Math.hypot(
          measureEnd[0] - from[0],
          measureEnd[1] - from[1],
        );
        placeLabelledLine(
          measureLine,
          viewport.worldToScreen(from),
          viewport.worldToScreen(measureEnd),
          formatLength(distance, format),
        );
        // A fixed measurement stops chasing the cursor.
        measureLine.line.setAttribute('stroke-dasharray', to ? 'none' : '6 4');
      }

      const snapScreen =
        isMeasuring && pointerState.snap
          ? viewport.worldToScreen(pointerState.snap.point)
          : null;
      show(snapRing, Boolean(snapScreen));
      if (snapScreen) {
        setAttributes(snapRing, { cx: snapScreen[0], cy: snapScreen[1] });
      }
    };

    return bindOverlayFrame({
      draw,
      subscriptions: [
        viewport.subscribe,
        viewport.size.subscribe,
        pointer.subscribe,
        measure.subscribe,
        tool.subscribe,
      ],
      onCleanup: () => svg.replaceChildren(),
    });
  }, [measure, tool, pointer, viewport, format]);

  return svgRef;
};
